
import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { LoadingSpinner } from './supabase-data/LoadingAndError';
import { getUserLocation } from '@/utils/userSettingsUtils';
import { useIsMobile } from '@/hooks/use-mobile';
import { ALL_ZMANIM, formatTime } from '@/utils/zmanimUtils';
import DisplayCard from '@/components/shared/DisplayCard';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_LOCATION } from '@/data/israelCities';

interface ZmanimCardsPageProps {
  selectedZmanim?: string[];
}

interface ChabadZmanimResponse {
  date: string;
  location: string;
  locationId: number;
  halachic_day: boolean;
  zmanim: Record<string, string>;
  source: string;
  error?: string;
}

const ZmanimCardsPage: React.FC<ZmanimCardsPageProps> = ({
  selectedZmanim
}) => {
  const isMobile = useIsMobile();
  const [zmanim, setZmanim] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zmanimSource, setZmanimSource] = useState<string>('');

  useEffect(() => {
    const fetchZmanim = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get user location from settings
        const location = await getUserLocation();
        const locationId = location.locationId || DEFAULT_LOCATION.id;
        const today = new Date().toISOString().split('T')[0];
        console.log('Fetching zmanim for locationId:', locationId);

        // Try enhanced Chabad.org function
        try {
          const {
            data: chabadData,
            error: chabadError
          } = await supabase.functions.invoke('chabad-zmanim', {
            body: {
              locationId: locationId,
              date: today,
              lat: parseFloat(location.lat || DEFAULT_LOCATION.lat.toString()),
              lng: parseFloat(location.lng || DEFAULT_LOCATION.lng.toString()),
              tzid: location.tzid || 'Asia/Jerusalem'
            }
          });
          if (chabadData && !chabadError) {
            const zmanimResponse = chabadData as ChabadZmanimResponse;
            console.log('Received enhanced Chabad zmanim:', zmanimResponse);
            if (zmanimResponse.zmanim && Object.keys(zmanimResponse.zmanim).length > 0) {
              setZmanim(zmanimResponse.zmanim);
              setZmanimSource(`${zmanimResponse.source} - ${zmanimResponse.location}`);
              setError(null);
              setLoading(false);
              return;
            }
          }
        } catch (chabadError) {
          console.log('Enhanced Chabad API failed, falling back to original zmanim function:', chabadError);
        }

        // Fallback to original zmanim function
        if (!location.lat || !location.lng) {
          // Use default location coordinates
          location.lat = DEFAULT_LOCATION.lat.toString();
          location.lng = DEFAULT_LOCATION.lng.toString();
          location.name = DEFAULT_LOCATION.name;
        }
        const {
          data,
          error: zmanimError
        } = await supabase.functions.invoke('zmanim', {
          body: {
            date: today,
            lat: parseFloat(location.lat),
            lng: parseFloat(location.lng),
            tzid: location.tzid || 'Asia/Jerusalem'
          }
        });
        if (zmanimError) {
          throw new Error(zmanimError.message || 'Failed to fetch zmanim data');
        }
        if (!data || !data.times) {
          throw new Error('Invalid zmanim data received');
        }
        setZmanim(data.times);
        setZmanimSource(`חישוב פנימי - ${location.name || 'מיקום לא ידוע'}`);
        setError(null);
      } catch (err) {
        console.error('Error fetching zmanim:', err);
        setError('שגיאה בטעינת זמני היום');
      } finally {
        setLoading(false);
      }
    };
    fetchZmanim();
  }, []);

  if (loading) {
    return <LoadingSpinner isLoading={loading} />;
  }

  if (error) {
    return <div className="max-w-full px-4">
        <Alert className="mb-6">
          <AlertTitle>שגיאה</AlertTitle>
          <AlertDescription>
            <p className="mb-2">{error}</p>
            <p>יש להגדיר מיקום בלוח הבקרה כדי להציג את זמני היום.</p>
          </AlertDescription>
        </Alert>
      </div>;
  }

  if (!zmanim) {
    return null;
  }

  // Map zmanim keys to display
  const zmanimToShow = selectedZmanim && selectedZmanim.length > 0 ? ALL_ZMANIM.filter(z => selectedZmanim.includes(z.key)) : ALL_ZMANIM;

  // Helper function to get zman value with fallback
  const getZmanTime = (key: string): string => {
    // Direct mapping
    if (zmanim[key]) return zmanim[key];

    // Alternative key mappings
    const keyMappings: Record<string, string[]> = {
      'alot': ['alot_hashachar', 'alot', 'dawn'],
      'sunrise': ['sunrise', 'haneitz', 'זריחה'],
      'sof_zman_shema_gra': ['sof_zman_shema_gra', 'shema_gra', 'latest_shema'],
      'sof_zman_shema_mga': ['sof_zman_shema_mga', 'shema_mga'],
      'sof_zman_tefila_gra': ['sof_zman_tefila_gra', 'tefila_gra', 'latest_tefila'],
      'sof_zman_tefila_mga': ['sof_zman_tefila_mga', 'tefila_mga'],
      'chatzot': ['chatzot', 'midday', 'חצות'],
      'mincha_gedola': ['mincha_gedola', 'earliest_mincha'],
      'mincha_ketana': ['mincha_ketana', 'preferred_mincha'],
      'plag_hamincha': ['plag_hamincha', 'plag'],
      'sunset': ['sunset', 'shkiah', 'שקיעה'],
      'tzeit': ['tzeit_hakochavim', 'tzeit', 'nightfall']
    };

    const alternatives = keyMappings[key] || [key];
    for (const altKey of alternatives) {
      if (zmanim[altKey]) return zmanim[altKey];
    }

    return '--:--';
  };

  return <div className="max-w-full mx-auto px-4 py-4 max-h-screen overflow-hidden">
      {/* Responsive grid ensuring all cards fit on screen - updated to 5 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 max-h-[calc(100vh-100px)] overflow-hidden">
        {zmanimToShow.map(({
        key,
        label
      }) => <DisplayCard key={key} title={label} time={formatTime(getZmanTime(key))} dividerPosition="afterTitle" />)}
      </div>
    </div>;
};

export default ZmanimCardsPage;
